/**
 * The one "guard a write" routine the three View-as backstops share (M4S-5670,
 * docs/pdlc/cs-view-as-client.md §6 "Write backstops").
 *
 * The axios interceptor, the GraphQL client and the same-origin fetch wrapper
 * each call `guardViewAsWrite` before a request leaves the tab, and
 * `recordViewAsWrite` once it has come back 2xx. The rule itself is
 * `isViewAsWriteAllowed` (pure, `write-policy.ts`); this file adds the tab's
 * state, the freshness check, the toast and the refused row.
 *
 * It never turns an API response into an end of View-as: it only acts before
 * the request is sent. A 403 or a credits `ApiDenialError` from the API reaches
 * the caller unchanged (§4 step 5).
 */

import { toast } from 'react-toastify';

import {
    ensureViewAsFresh,
    recordViewAsAction,
    recordViewAsRefusal,
} from '@/@subsidiary/view-as-lifecycle';
import { getViewAsSnapshot } from '@/@subsidiary/view-as-store';
import {
    isViewAsWriteAllowed,
    normaliseViewAsPath,
    viewAsRefusalMessage,
    ViewAsWriteRefusedError,
    type ViewAsWriteDecision,
    type ViewAsWriteInput,
} from '@/lib/view-as/write-policy';

/** One toast id, so a burst of refused writes (an autosave loop) shows one notice. */
export const VIEW_AS_REFUSED_TOAST_ID = 'view-as-write-refused';

const READ_METHODS = new Set(['GET', 'HEAD']);

/** What a backstop needs to record a write after it succeeds. */
export interface GuardedWrite {
    decision: ViewAsWriteDecision;
    client: ViewAsWriteInput['client'];
    method: string;
    path: string;
}

/**
 * Guard one request. Resolves with what to record on a 2xx (or null when there
 * is nothing to record), or rejects with `ViewAsWriteRefusedError`:
 *
 * - no View-as session in the tab: pass, nothing recorded;
 * - GET or HEAD: pass (a recorded read, the document upload's URL mint, is
 *   returned so it can be recorded);
 * - the session is not `active` (restoring, starting or ending): refuse,
 *   reason `session`, no toast;
 * - the policy refuses: toast, a best-effort `refused` row, reason `policy`;
 *   View-as stays on;
 * - allowed: await `ensureViewAsFresh()`; when it fails (it runs the end path
 *   itself), refuse with reason `session` and no toast.
 */
export async function guardViewAsWrite(input: ViewAsWriteInput): Promise<GuardedWrite | null> {
    const snapshot = getViewAsSnapshot();
    if (!snapshot.sessionId) return null;

    const method = String(input.method || 'GET').toUpperCase();
    const path = normaliseViewAsPath(input.path);
    const refusal = (reason: 'policy' | 'session', message?: string) =>
        new ViewAsWriteRefusedError({ reason, client: input.client, method, path, message });

    if (READ_METHODS.has(method)) {
        const decision = isViewAsWriteAllowed({ ...input, method, path });
        return decision.allowed && decision.record !== 'none'
            ? { decision, client: input.client, method, path }
            : null;
    }

    const decision = isViewAsWriteAllowed({ ...input, method, path });

    // Telemetry, preferences and compute (allowed, never recorded) touch no
    // client data, so they pass in any session state: a route log or the staff
    // member's own demo token fired while a session is restoring is not a
    // client write and must not throw.
    if (decision.allowed && decision.record === 'none') return null;

    if (snapshot.status !== 'active') throw refusal('session');

    if (!decision.allowed) {
        const message = viewAsRefusalMessage(snapshot.orgName);
        toast.error(message, { toastId: VIEW_AS_REFUSED_TOAST_ID });
        recordViewAsRefusal({
            source: 'backstop',
            client: input.client,
            method,
            path,
            ...(decision.kind ? { kind: decision.kind } : {}),
            ...(input.operation ? { operation: input.operation } : {}),
        });
        throw refusal('policy', message);
    }

    if (!(await ensureViewAsFresh())) throw refusal('session');
    return { decision, client: input.client, method, path };
}

/**
 * Record an allowed write that came back 2xx. `data_export` rows are written
 * every time; `api_write` rows are throttled per ref by the lifecycle.
 * Telemetry and preferences (`record: 'none'`) write nothing.
 */
export function recordViewAsWrite(
    guarded: GuardedWrite | null | undefined,
    status: number,
    extra: Record<string, unknown> = {},
): void {
    if (!guarded || status < 200 || status > 299) return;
    const { decision, client, method, path } = guarded;
    const record = decision.record;
    if (record === 'none') return;
    try {
        recordViewAsAction(record, decision.ref, {
            ...(decision.kind ? { kind: decision.kind } : {}),
            client,
            method,
            path,
            ...(decision.detail ?? {}),
            ...extra,
        });
    } catch (error) {
        // Fail-open: a lost row never fails the write it describes.
        console.warn('[view-as] action row not recorded', error);
    }
}
